import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { shippingApi } from "../../shared/shippingApi";
import type { SaveShipmentPayload, ShipmentOrder, ShipmentOrderSummary } from "../../types/shipping";

const emptyMeta = { invoice_number: "", seal_number: "", notes: "" };
const quantityText = (value: number) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });

export default function CreateShipping() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState<ShipmentOrderSummary[]>([]);
  const [orderId, setOrderId] = useState(params.get("orderId") || "");
  const [draftId, setDraftId] = useState(params.get("draftId") || "");
  const [order, setOrder] = useState<ShipmentOrder | null>(null);
  const [quantities, setQuantities] = useState<Record<number, string>>({});
  const [meta, setMeta] = useState(emptyMeta);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    shippingApi
      .orders()
      .then((result) =>
        result.success
          ? setOrders(result.data.orders || [])
          : setError(result.data?.message || "Could not load orders.")
      );
  }, []);
  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      return;
    }
    setLoading(true);
    setError("");
    Promise.all([
      shippingApi.order(orderId),
      draftId ? shippingApi.draft(draftId) : Promise.resolve(null),
    ]).then(([orderResult, draftResult]) => {
      if (!orderResult.success) setError(orderResult.data?.message || "Could not load order.");
      else setOrder(orderResult.data);
      if (draftResult?.success) {
        const draft = draftResult.data;
        setQuantities(
          Object.fromEntries(
            (draft.items || []).map((item) => [item.order_item_id, String(item.quantity)])
          )
        );
        setMeta({
          invoice_number: draft.invoice_number || "",
          seal_number: draft.seal_number || "",
          notes: draft.notes || "",
        });
      }
      setLoading(false);
    });
  }, [orderId, draftId]);

  const selected = useMemo(
    () => Object.entries(quantities).filter(([, quantity]) => Number(quantity) > 0),
    [quantities]
  );
  const payload = (): SaveShipmentPayload => ({
    order_id: Number(orderId),
    ...meta,
    items: selected.map(([id, quantity]) => ({
      order_item_id: Number(id),
      quantity: Number(quantity),
    })),
  });
  const validate = () => {
    if (!orderId || selected.length === 0) {
      setError("Select an order and enter at least one shipped quantity.");
      return false;
    }
    const invalid = order?.items?.find(
      (item) =>
        Number(quantities[item.id] || 0) > item.ordered_quantity - item.shipped_quantity ||
        Number(quantities[item.id] || 0) < 0
    );
    if (invalid) {
      setError(`${invalid.product_name} exceeds its remaining quantity.`);
      return false;
    }
    return true;
  };
  const saveDraft = async () => {
    if (!validate()) return;
    setLoading(true);
    setError("");
    const result = await shippingApi.saveDraft(payload());
    if (result.success) {
      setDraftId(String(result.data.id));
      setMessage("In-progress shipment saved.");
    } else
      setError(
        result.data?.message ||
          Object.values(result.data?.errors || {}).flat()[0] ||
          "Could not save the in-progress shipment."
      );
    setLoading(false);
  };
  const finalize = async () => {
    if (!validate()) return;
    setLoading(true);
    setError("");
    let result;
    if (draftId) {
      const saved = await shippingApi.saveDraft(payload());
      result = saved.success ? await shippingApi.finalizeDraft(saved.data.id) : saved;
    } else result = await shippingApi.create(payload());
    if (result.success) navigate(`/shippings/${result.data.id}`);
    else
      setError(
        result.data?.message ||
          Object.values(result.data?.errors || {}).flat()[0] ||
          "Could not finalize shipment."
      );
    setLoading(false);
  };
  return (
    <div className="p-6 space-y-5">
      <section className="sticky top-0 z-10 rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              Outbound session
            </p>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">
              {draftId ? "Continue shipment" : "Create shipment"}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Quantities do not affect inventory until the shipment is marked Shipped.
            </p>
            {draftId && (
              <div className="mt-3">
                <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                  IN PROGRESS
                </span>
              </div>
            )}
          </div>
          <div className="text-right">
            <p className="font-mono text-lg font-semibold tabular-nums text-gray-900">
              {quantityText(selected.reduce((sum, [, value]) => sum + Number(value), 0))} units
            </p>
            <p className="text-xs text-gray-500">{selected.length} shipment lines</p>
          </div>
        </div>
      </section>
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">{error}</p>
      )}
      {message && (
        <p className="rounded-lg bg-green-50 p-3 text-sm font-medium text-green-700">{message}</p>
      )}
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <label className="text-sm font-semibold text-gray-700">
          Order
          <select
            disabled={Boolean(draftId)}
            value={orderId}
            onChange={(e) => {
              setOrderId(e.target.value);
              setQuantities({});
            }}
            className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="">Select an order</option>
            {orders.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
      </section>
      {loading && !order ? (
        <p className="py-10 text-center text-gray-500">Loading shipment…</p>
      ) : (
        order && (
          <>
            <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="border-b border-gray-200 p-5">
                <h2 className="font-semibold text-gray-900">{order.customer}</h2>
                <p className="text-sm text-gray-500">
                  {order.po_number ? `PO ${order.po_number}` : `Order #${order.id}`}
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Product
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Ordered
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Already shipped
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Remaining
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Ship now
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {order.items.map((item) => {
                      const remaining = Math.max(0, item.ordered_quantity - item.shipped_quantity);
                      return (
                        <tr key={item.id}>
                          <td className="px-5 py-4">
                            <p className="font-semibold text-gray-900">{item.product_name}</p>
                            <p className="text-xs text-gray-500">SKU {item.sku || "—"}</p>
                          </td>
                          <td className="px-5 py-4 text-right font-mono tabular-nums">
                            {quantityText(item.ordered_quantity)}
                          </td>
                          <td className="px-5 py-4 text-right font-mono tabular-nums">
                            {quantityText(item.shipped_quantity)}
                          </td>
                          <td className="px-5 py-4 text-right font-mono tabular-nums">
                            {quantityText(remaining)}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <input
                              type="number"
                              min="0"
                              max={remaining}
                              step="0.0001"
                              value={quantities[item.id] || ""}
                              onChange={(e) =>
                                setQuantities({ ...quantities, [item.id]: e.target.value })
                              }
                              className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-right font-mono tabular-nums"
                              placeholder="0"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="font-semibold text-gray-900">Shipment references</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="text-sm font-medium text-gray-700">
                  Invoice number
                  <input
                    value={meta.invoice_number}
                    onChange={(e) => setMeta({ ...meta, invoice_number: e.target.value })}
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Seal number
                  <input
                    value={meta.seal_number}
                    onChange={(e) => setMeta({ ...meta, seal_number: e.target.value })}
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </label>
                <label className="text-sm font-medium text-gray-700 md:col-span-2">
                  Notes
                  <textarea
                    value={meta.notes}
                    onChange={(e) => setMeta({ ...meta, notes: e.target.value })}
                    rows={3}
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                </label>
              </div>
              <div className="mt-5 flex flex-col justify-end gap-3 sm:flex-row">
                <button
                  disabled={loading || selected.length === 0}
                  onClick={saveDraft}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Save in progress
                </button>
                <button
                  disabled={loading || selected.length === 0}
                  onClick={finalize}
                  className="rounded-lg bg-main-purple px-4 py-2 font-semibold text-white hover:bg-indigo-600 disabled:opacity-50"
                >
                  {loading ? "Working…" : "Mark shipped"}
                </button>
              </div>
            </section>
          </>
        )
      )}
    </div>
  );
}
